// S9 Applications (T-403): what the organizer sees about applicants and participants.
import { z } from 'zod';

import { eventIdParamsSchema } from './events.js';
import { localizedNameSchema } from './localized.js';
import { userStatsSchema } from './profile.js';

/** Reviews needed before an average rating is shown (PROJECT → Reviews). */
export const MIN_REVIEWS_FOR_RATING = 3;
/** The pending list is FIFO and capped; `pendingTruncated` says more are waiting. */
export const APPLICATIONS_PAGE_LIMIT = 200;
/** No-shows can be marked after the end, within the review window (D20). */
export const NO_SHOW_WINDOW_DAYS = 14;

/** Applicant profile card. Never contains birth date, Telegram username or contacts. */
export const applicantSchema = z.object({
	applicationId: z.uuid(),
	status: z.enum(['pending', 'accepted']),
	message: z.string().nullable(),
	noShow: z.boolean(),
	appliedAt: z.iso.datetime(),
	user: z.object({
		id: z.uuid(),
		/** Null: the account was deleted (T-804). */
		displayName: z.string().nullable(),
		photoUrl: z.url().nullable(),
		age: z.number().int().nullable(),
		about: z.string().nullable(),
		interests: z.array(z.object({ slug: z.string(), name: localizedNameSchema })),
	}),
	stats: userStatsSchema,
});

/** `GET /v1/events/:id/applications` and the response of every organizer decision. */
export const eventApplicationsSchema = z.object({
	eventId: z.uuid(),
	title: z.string(),
	/** Includes the organizer (D17). */
	seats: z.object({ taken: z.number().int(), max: z.number().int() }),
	pending: z.array(applicantSchema),
	pendingTruncated: z.boolean(),
	participants: z.array(applicantSchema),
	/** What the organizer can do right now; the API enforces the same rules. */
	actions: z.object({ decide: z.boolean(), remove: z.boolean(), markNoShow: z.boolean() }),
});

export const participantParamsSchema = eventIdParamsSchema.extend({ userId: z.uuid() });
export const noShowRequestSchema = z.strictObject({ noShow: z.boolean() });

export type Applicant = z.infer<typeof applicantSchema>;
export type EventApplications = z.infer<typeof eventApplicationsSchema>;
